import { ICommonSettings, ISearchEngineSettings } from "@kuika/kuika-cl-model/src/models/common-config";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import _ from "lodash";
import * as React from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSwitch from "../../../../ui-kit/kuika-switch/kuika-switch";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter, ConfigModalGroupHeading } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigSearchEngineSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingSetting?: ISearchEngineSettings;
}

export interface IConfigSearchEngineSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingSearchEngineId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigSearchEngineSettings extends React.PureComponent<
  IConfigSearchEngineSettingsProps,
  IConfigSearchEngineSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigSearchEngineSettingsProps) {
    super(props);
    this.state = { saveStatus: "idle", deleteStatus: "idle", editingSetting: undefined };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigSearchEngineSettingsProps) {
    if (prevProps.editingSearchEngineId && !this.props.editingSearchEngineId) {
      this.formRef.current?.resetFields();
    }
    if (prevProps.editingSearchEngineId !== this.props.editingSearchEngineId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
      return;
    }
    if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (!this.props.editingSearchEngineId) {
      this.setState({ editingSetting: undefined }, () => {
        this.formRef.current?.resetFields();
        this.formRef.current?.setFieldsValue({
          provider: "OpenSearch",
          defaultIndexPrefix: "kuika",
          tlsVerify: true,
          connectionTimeoutSeconds: 10,
          maxResultsPerQuery: 200,
          highlightFragmentSize: 150
        });
      });
      return;
    }

    const list = _.cloneDeep(this.props.commonSettings?.searchEngineSettings) || [];
    const editingSetting = list.find((s) => s.id === this.props.editingSearchEngineId);
    if (!editingSetting) {
      this.setState({ editingSetting: undefined }, () => {
        this.formRef.current?.resetFields();
      });
      return;
    }

    this.setState({ editingSetting }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue({ ...editingSetting });
    });
  };

  existingNameValidator = async (_rule: any, value: string) => {
    const list = this.props.commonSettings?.searchEngineSettings || [];
    const isDuplicate = list.some((x) => x.name === value && x.id !== this.props.editingSearchEngineId);
    if (isDuplicate) throw new Error("Search Engine Setting name already exists.");
  };

  onFinish = async (data: any) => {
    this.saveStatus.start();

    const obj: ISearchEngineSettings = {
      id:
        this.props.editingSearchEngineId && this.props.editingSearchEngineId !== ""
          ? this.props.editingSearchEngineId
          : Guid.create().toString(),
      name: data.name,
      enabled: true,
      provider: data.provider || "OpenSearch",
      url: data.url?.trim() || "",
      username: data.username || "",
      password: data.password || "",
      defaultIndexPrefix: data.defaultIndexPrefix || "kuika",
      tlsVerify: data.tlsVerify !== false,
      connectionTimeoutSeconds: data.connectionTimeoutSeconds ?? 10,
      maxResultsPerQuery: data.maxResultsPerQuery ?? 200,
      highlightFragmentSize: data.highlightFragmentSize ?? 150
    };

    const saveParamsBase = {
      formInputName: this.props.formInputName,
      config: obj,
      configKey: "searchEngineSettings" as const,
      onError: () => this.saveStatus.fail(),
      onFinal: () => {}
    };

    if (this.props.editingSearchEngineId) {
      this.props.handleUpdateCommonConfig({
        ...saveParamsBase,
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        }
      });
      return;
    }

    this.props.handleCreateCommonConfig({
      ...saveParamsBase,
      onSuccess: () => {
        this.saveStatus.succeed(() => {
          this.props.closeModal();
        });
      }
    });
  };

  deleteSetting = () => {
    if (!this.props.editingSearchEngineId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingSearchEngineId || "",
      configKey: "searchEngineSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification("Search Engine setting has been deleted");
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Full-Text Search Engine setting"
        secondHeader="Full-Text Search Engine setting"
        width={720}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingSearchEngineId ? this.deleteSetting : undefined}
            submitLabel={this.props.editingSearchEngineId ? "UPDATE" : "CREATE"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configSearchEngineForm"
          />
        }
      >
        <Form
          id="configSearchEngineForm"
          name="searchEngineSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          style={{ height: "100%" }}
        >
          <ConfigModalBody>
            {/* Name */}
            <ConfigField label="Name" required tooltipContentKey="appsettings_searchengine_name">
              <Form.Item
                name="name"
                rules={[{ required: true, message: "Please enter a name." }, { validator: this.existingNameValidator }]}
              >
                <KuikaInput size="md" autoFocus />
              </Form.Item>
            </ConfigField>

            {/* Provider */}
            <ConfigField label="Provider" required tooltipContentKey="appsettings_searchengine_provider">
              <Form.Item name="provider">
                <KuikaSelectbox
                  size="md"
                  options={[
                    { value: "OpenSearch", label: "OpenSearch" },
                    { value: "ElasticSearch", label: "ElasticSearch" },
                    { value: "Meilisearch", label: "Meilisearch" }
                  ]}
                />
              </Form.Item>
            </ConfigField>

            {/* Provider info box */}
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev?.provider !== cur?.provider}>
              {({ getFieldValue }) => {
                const provider = getFieldValue("provider") || "OpenSearch";
                const isMeilisearch = provider === "Meilisearch";

                const urlPlaceholder = isMeilisearch
                  ? "http://localhost:7700"
                  : provider === "ElasticSearch"
                    ? "https://elasticsearch:9200"
                    : "https://opensearch:9200";

                const authLabel = isMeilisearch ? "API Key" : "Username";
                const authPlaceholder = isMeilisearch ? "masterKey_xxxxxxxx" : "admin";

                return (
                  <>
                    {/* Connection URL */}
                    <ConfigField label="Connection URL" required tooltipContentKey="appsettings_searchengine_url">
                      <Form.Item name="url" rules={[{ required: true, message: "Connection URL is required." }]}>
                        <KuikaInput size="md" placeholder={urlPlaceholder} />
                      </Form.Item>
                    </ConfigField>

                    {/* Auth — Username/API Key */}
                    {/* Only Meilisearch demands this one — the others accept an empty user. */}
                    <ConfigField
                      label={authLabel}
                      required={isMeilisearch}
                      tooltipContentKey={
                        isMeilisearch ? "appsettings_searchengine_apikey" : "appsettings_searchengine_username"
                      }
                    >
                      <Form.Item
                        name="username"
                        rules={
                          isMeilisearch ? [{ required: true, message: "API Key is required for Meilisearch." }] : []
                        }
                      >
                        <KuikaInput size="md" placeholder={authPlaceholder} />
                      </Form.Item>
                    </ConfigField>

                    {/* Password — only for OpenSearch/ElasticSearch */}
                    {!isMeilisearch && (
                      <ConfigField label="Password" tooltipContentKey="appsettings_searchengine_password">
                        <Form.Item name="password">
                          <KuikaInput size="md" type="password" />
                        </Form.Item>
                      </ConfigField>
                    )}
                  </>
                );
              }}
            </Form.Item>

            {/* Advanced Settings */}
            <ConfigModalGroupHeading>Advanced Settings</ConfigModalGroupHeading>

            <ConfigField label="Default Index Prefix" tooltipContentKey="appsettings_searchengine_indexprefix">
              <Form.Item name="defaultIndexPrefix">
                <KuikaInput size="md" placeholder="kuika" />
              </Form.Item>
            </ConfigField>

            <ConfigField label="Verify TLS Certificate" tooltipContentKey="appsettings_searchengine_tlsverify">
              <Form.Item name="tlsVerify" valuePropName="checked" initialValue={true}>
                <KuikaSwitch />
              </Form.Item>
            </ConfigField>

            <ConfigField label="Connection Timeout (seconds)" tooltipContentKey="appsettings_searchengine_timeout">
              <Form.Item name="connectionTimeoutSeconds">
                <KuikaInput size="md" type="number" min={1} max={60} placeholder="10" />
              </Form.Item>
            </ConfigField>

            <ConfigField label="Max Results Per Query" tooltipContentKey="appsettings_searchengine_maxresults">
              <Form.Item name="maxResultsPerQuery">
                <KuikaInput size="md" type="number" min={10} max={1000} placeholder="200" />
              </Form.Item>
            </ConfigField>

            <ConfigField label="Highlight Fragment Size" tooltipContentKey="appsettings_searchengine_highlightsize">
              <Form.Item name="highlightFragmentSize">
                <KuikaInput size="md" type="number" min={50} max={500} placeholder="150" />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
